import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@shared/api/client";

export type PresupuestoEstado = "borrador" | "enviado" | "aceptado" | "rechazado" | "facturado";

export interface PresupuestoLinea {
  id?: number;
  concepto: string | null;
  descripcion: string;
  cantidad: number;
  precio_unitario: number;
  iva_porcentaje: number;
  total_linea?: number;
}

export interface Presupuesto {
  id: number;
  numero: string;
  emisor_id: number;
  cliente_id: number;
  fecha: string;
  validez_hasta: string | null;
  subtotal: number;
  iva: number;
  total: number;
  estado: PresupuestoEstado;
  notas: string | null;
  factura_id: number | null;
  emisor?: { id: number; nombre: string; color_hex?: string };
  cliente?: { id: number; nombre: string };
  lineas?: PresupuestoLinea[];
}

export interface PresupuestoPayload {
  emisor_id: number;
  cliente_id: number;
  fecha?: string;
  validez_hasta?: string | null;
  notas?: string | null;
  lineas: Omit<PresupuestoLinea, "id" | "total_linea">[];
}

const BASE = "/billing/quotes";
const key = ["presupuestos"] as const;

export function usePresupuestos(estado?: string) {
  return useQuery({
    queryKey: [...key, estado ?? "all"],
    queryFn: async () =>
      (await api.get<{ data: Presupuesto[] }>(BASE, { params: { estado: estado || undefined } })).data.data,
  });
}

export function useCreatePresupuesto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: PresupuestoPayload) =>
      (await api.post<{ data: Presupuesto }>(BASE, payload)).data.data,
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  });
}

export function useSetPresupuestoEstado() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, estado }: { id: number; estado: PresupuestoEstado }) =>
      (await api.post(`${BASE}/${id}/estado`, { estado })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  });
}

export function useConvertPresupuesto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) =>
      (await api.post<{ ok: boolean; factura_numero: string }>(`${BASE}/${id}/convertir`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: key });
      qc.invalidateQueries({ queryKey: ["facturas"] });
    },
  });
}

export function useDeletePresupuesto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => (await api.delete(`${BASE}/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  });
}

export async function openPresupuestoPdf(id: number): Promise<void> {
  const res = await api.get(`${BASE}/${id}/pdf`, { responseType: "blob" });
  const url = window.URL.createObjectURL(res.data);
  window.open(url, "_blank");
}
